import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, someTween } from '../skillUsage';

export const continuousPushInSkill: CodeoSkill = {
    name: SKILL_NAMES.continuousPushIn,
    kind: 'codeo',
    description:
        'One slow, linear camera push-in that carries straight through a run of hard cuts, so ' +
        'three or four one-word beats on different grounds read as one shot moving forward. For ' +
        'a list of verbs or steps said in a row.',
    checkLooksFor: 'a wrapper scaled up past 1 with ease none, spanning several cuts',
    documentUsesSkill: check((script) =>
        someTween(script, /\bscale\b/, /ease:\s*['"](?:none|linear)['"]/),
    ),
    instructions: `# Continuous Push-In

A run of beats ("Reads. Thinks. Writes. Cuts.") cuts hard from ground to ground, one word each, but the camera never stops: everything sits inside one wrapper that **scales up at a constant, slow rate from the run's first frame to its last**. Each cut lands on a frame that is slightly closer than the one before it, so the eye reads the run as a single shot pushing forward while the grounds change around it.

The push is linear and slow, about 4–5% per second. It is not an ease and not a zoom-through: it never accelerates and never settles. Each word still pops in and settles on its own inside the moving frame.

## Markup

All the run's beats live inside one camera wrapper. Each beat is a full-frame layer with its own ground, hidden until its cut.

\`\`\`html
<div id="camera" style="position: absolute; inset: 0; transform-origin: 50% 50%;">
  <div class="beat" id="beat-reads" style="position: absolute; inset: 0; background: var(--l5-color-1, #f6f4f0);">
    <div class="absolute inset-0 flex items-center justify-center"><div class="hero" style="text-align: center; line-height: 1; font-family: var(--l5-font-0), sans-serif; font-size: 330px; font-weight: 500; color: var(--l5-color-0, #14121f);"><span class="w">Reads</span></div></div>
  </div>
  <div class="beat" id="beat-thinks" style="position: absolute; inset: 0; background: var(--l5-color-2, #4b3ae6);">
    <div class="absolute inset-0 flex items-center justify-center"><div class="hero" style="text-align: center; line-height: 1; font-family: var(--l5-font-0), sans-serif; font-size: 330px; font-weight: 500; color: var(--l5-color-1, #f6f4f0);"><span class="w">Thinks</span></div></div>
  </div>
</div>
\`\`\`

\`\`\`css
.w { display: inline-block; }
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const SCENE = 1.7;
const RUN_START = 0;
const RUN_END = 1.7;
const PUSH_PER_SECOND = 0.046;              // 4.6% closer every second
const CUTS = [0, 0.92];                     // each beat's cue

// The camera: one linear tween across every cut in the run.
tl.fromTo('#camera', { scale: 1 }, {
  scale: 1 + (RUN_END - RUN_START) * PUSH_PER_SECOND, duration: RUN_END - RUN_START, ease: 'none',
}, RUN_START);

// The cuts: the first beat is up from the first frame; each later one shows on its cue and the
// one before hides on the same frame.
const beats = gsap.utils.toArray('.beat');
gsap.set(beats.slice(1), { autoAlpha: 0 });
beats.forEach((beat, i) => {
  if (i > 0) {
    tl.set(beat, { autoAlpha: 1 }, CUTS[i]);
    tl.set(beats[i - 1], { autoAlpha: 0 }, CUTS[i]);
  }
  const word = beat.querySelector('.w');
  const em = parseFloat(getComputedStyle(word).fontSize);
  tl.fromTo(word, { y: 0.72 * em }, { y: 0, duration: 0.62, ease: 'expo.out' }, CUTS[i]);
});

tl.set({}, {}, SCENE);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Rate.** 3–6% per second. Slower than 3% is not seen; faster than 6% turns into a zoom and fights the words settling.
- **A single held phrase.** A phrase that holds longer than a second ("HOURS of editing") can take the same push on its own, at about 4% per second, so the hold is never a still frame.
- **Run length.** Three or four beats, about a second each. A longer run should change ground colours in a pattern (light, accent, mesh, dark) so the push has something to carry across.
- **Exit.** The last beat of the run may lift out with \`${SKILL_NAMES.wordPopSettle}\`'s lift; the push keeps running under it until the cut.

## Pitfalls

- **Flat grounds, one word.** Each beat is its ground colour and its word, nothing else: no streaks, rules, particles or texture. The push is read from the type growing; decoration around it only competes with it.
- **One tween, not one per beat.** Restarting the scale at each cut makes the camera jump back; the point is that it never does.
- **Ease none.** An eased push settles, and a settled camera at a cut reads as a new shot.
- The camera wrapper is transform-tweened, so it carries no CSS \`transform\`; set its \`transform-origin\` in CSS.
- Keep the grounds inside the wrapper. A ground outside it stays still and gives the push away as a scaling layer.`,
};
